import {
  type MutableRefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'

import { getAbsoluteOffset } from '@lib/dom'
import {
  type CombinedListingProductType,
  type CombinedListingStep,
  type CombinedListingToggleStepHandler,
  getCombinedListingProductDefaultValues,
  getCombinedListingProductDropdownOptions,
  getCombinedListingProductsBySize,
  ProductCombinedListingContext,
} from '@lib/product/combined-listing'
import { type DropdownOption, getOptionTitle } from '@lib/product/option'
import { ProductQuickAddContext } from '@lib/product/quick-add-context'
import { SiteContext } from '@lib/site-context'
import { getSizeDimensionsFromString } from '@lib/size'
import { StringsContext } from '@lib/strings-context'

import Accordion from '@components/accordion'
import Button from '@components/buttons/button'
import Link from '@components/link'
import { PASSEPARTOUT_MIN_VALUE } from './combined-listing-product-passepartout-select'

interface CombinedListingProductSizeSelectProps {
  isDefaultSelectionLoadedRef: MutableRefObject<boolean>
  glassProductId?: number
  backboardProductId?: number
  passepartoutProductId?: number
  spacerProductId?: number
  openStep?: CombinedListingStep
  isScreenSmallOrLarger: boolean
  setPassepartoutHoleWidth: (width: number | null) => void
  setPassepartoutHoleHeight: (height: number | null) => void
  onToggleStep: CombinedListingToggleStepHandler
}

const CombinedListingProductSizeSelect = ({
  isDefaultSelectionLoadedRef,
  glassProductId,
  backboardProductId,
  passepartoutProductId,
  spacerProductId,
  openStep,
  isScreenSmallOrLarger,
  setPassepartoutHoleWidth,
  setPassepartoutHoleHeight,
  onToggleStep,
}: CombinedListingProductSizeSelectProps) => {
  const {
    combinedListingSettings,
    combinedListingConfiguration,
    combinedListingActiveSize,
    addCombinedListingProduct,
    clearCombinedListingProductType,
    setCombinedListingSize,
    setCombinedListingPassepartoutHoleSize,
  } = useContext(ProductCombinedListingContext)
  const { productQuickAdd } = useContext(ProductQuickAddContext)
  const { headerHeight } = useContext(SiteContext)
  const strings = useContext(StringsContext)

  const ref = useRef<HTMLDivElement | null>(null)

  const sizeOptions = useMemo<DropdownOption[]>(() => {
    const combinedListing = getCombinedListingProductsBySize(
      combinedListingConfiguration?.frame ?? [],
      combinedListingConfiguration?.glass ?? [],
      combinedListingConfiguration?.backboard ?? [],
      combinedListingConfiguration?.passepartout ?? [],
      combinedListingConfiguration?.spacer ?? [],
    )

    return Object.keys(combinedListing).map((size) => ({
      value: size,
      title: size,
    }))
  }, [combinedListingConfiguration])

  /**
   * Validates value for product type and deselects it, if it's invalid.
   */
  const validateProductType = useCallback(
    (type: CombinedListingProductType, size: string, value?: number) => {
      if (!value) {
        return
      }

      const options = getCombinedListingProductDropdownOptions(
        combinedListingConfiguration?.[type] ?? [],
        size,
      )
      const isValid = options.some((option) => option.value === `${value}`)

      // Deselect products that don't have a matching size
      if (!isValid) {
        clearCombinedListingProductType(type)
      }
    },
    [clearCombinedListingProductType, combinedListingConfiguration],
  )

  /**
   * Selects default glass, backboard, passepartout and spacer products.
   */
  const selectDefaults = useCallback(
    (size: string) => {
      // Select default values only once
      if (
        !combinedListingSettings ||
        !combinedListingConfiguration ||
        isDefaultSelectionLoadedRef.current
      ) {
        return
      }

      isDefaultSelectionLoadedRef.current = true

      // Stop if any product is already selected
      if (
        glassProductId ||
        backboardProductId ||
        passepartoutProductId ||
        spacerProductId
      ) {
        return
      }

      const defaultValues = getCombinedListingProductDefaultValues(
        combinedListingSettings,
        combinedListingConfiguration,
        size,
      )
      const defaultProductIds = [
        defaultValues.glassProductId,
        defaultValues.backboardProductId,
        defaultValues.passepartoutProductId,
        defaultValues.spacerProductId,
      ].filter(Boolean) as number[]

      defaultProductIds.forEach((productId) => {
        addCombinedListingProduct(productId)
      })
    },
    [
      addCombinedListingProduct,
      backboardProductId,
      combinedListingConfiguration,
      combinedListingSettings,
      glassProductId,
      isDefaultSelectionLoadedRef,
      passepartoutProductId,
      spacerProductId,
    ],
  )

  /**
   * Handles changes in selected size.
   */
  const handleSizeChange = useCallback(
    (newSize: string) => {
      if (!newSize) {
        return
      }

      const { width, height } = getSizeDimensionsFromString(newSize)

      const newPassepartoutHoleWidth = width - PASSEPARTOUT_MIN_VALUE
      const newPassepartoutHoleHeight = height - PASSEPARTOUT_MIN_VALUE

      setPassepartoutHoleWidth(newPassepartoutHoleWidth)
      setPassepartoutHoleHeight(newPassepartoutHoleHeight)

      if (passepartoutProductId) {
        setCombinedListingPassepartoutHoleSize(
          `${newPassepartoutHoleWidth}x${newPassepartoutHoleHeight}`,
        )
      }

      setCombinedListingSize(newSize)

      validateProductType('glass', newSize, glassProductId)
      validateProductType('backboard', newSize, backboardProductId)
      validateProductType('passepartout', newSize, passepartoutProductId)
      validateProductType('spacer', newSize, spacerProductId)

      selectDefaults(newSize)
    },
    [
      backboardProductId,
      glassProductId,
      passepartoutProductId,
      selectDefaults,
      setCombinedListingSize,
      setPassepartoutHoleHeight,
      setPassepartoutHoleWidth,
      spacerProductId,
      validateProductType,
      setCombinedListingPassepartoutHoleSize,
    ],
  )

  const handleToggle = useCallback(
    (isOpen: boolean) => {
      onToggleStep('size', isOpen)

      // Scrolling animation on mobile when opening the accordion
      if (isOpen && !isScreenSmallOrLarger && !productQuickAdd.isOpen) {
        setTimeout(() => {
          if (!ref.current) {
            return
          }

          const offsetTop = getAbsoluteOffset(ref.current).top
          window.scroll({
            top: Math.max(0, offsetTop - headerHeight),
            behavior: 'smooth',
          })
        }, 500)
      }
    },
    [headerHeight, isScreenSmallOrLarger, onToggleStep, productQuickAdd.isOpen],
  )

  const title = (
    <>
      {strings.combinedListingSize}:{' '}
      <span className="!font-normal">
        {combinedListingActiveSize
          ? getOptionTitle(sizeOptions, combinedListingActiveSize)
          : (strings.combinedListingSizePlaceholder ?? '')}
      </span>
    </>
  )

  return (
    <Accordion
      ref={ref}
      id="combined-listing-product-size"
      title={title}
      bordered
      open={openStep === 'size'}
      onToggle={handleToggle}
    >
      <div className="flex flex-wrap gap-2.5">
        {sizeOptions.map((option) => (
          <Button
            key={option.value}
            onClick={() => handleSizeChange(option.value)}
            size="small"
            variant={
              combinedListingActiveSize === option.value ? 'filled' : 'outlined'
            }
            color={
              combinedListingActiveSize === option.value ? 'black' : 'gray'
            }
          >
            {option.title}
          </Button>
        ))}
      </div>

      {combinedListingSettings?.configuratorLink && (
        <div className="border-t pt-4 mt-5">
          <Link
            link={combinedListingSettings?.configuratorLink}
            buttonSize="small"
          >
            {strings.combinedListingConfigureCustomFrame}
          </Link>
        </div>
      )}
    </Accordion>
  )
}

export default CombinedListingProductSizeSelect
